/**
 * Entry create/edit route — /app/entries/$id
 *
 * Protected by the _app.tsx beforeLoad auth guard.
 * $id = 'new' for create mode; a ULID string for edit mode.
 *
 * In edit mode, finds the existing entry from useEntriesQuery and passes it
 * to EntryForm for pre-population.
 *
 * Page background: Slate-50 (dominant 60%) — set by _app layout.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { EntryForm } from '@/features/entries/EntryForm';
import { useEntriesQuery } from '@/features/entries/entryApi';

function EntryFormPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: entries } = useEntriesQuery();

  // Wait for entries before deciding whether this is the first-run "Initial balance" screen,
  // to avoid flashing the full form first.
  if (entries === undefined) {
    return (
      <div className="flex min-h-[calc(100vh-72px)] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Find the existing entry when in edit mode ($id !== 'new')
  const existingEntry = id !== 'new' ? entries.find((e) => e.id === id) : undefined;
  // First-run: creating with no entries yet → simplified Initial balance screen.
  const isInitialBalance = id === 'new' && entries.length === 0;

  return (
    <div className="flex flex-col items-center justify-start min-h-[calc(100vh-72px)] px-4 py-8">
      <EntryForm
        entryId={id}
        existingEntry={existingEntry}
        initialDate={search.date}
        initialBalance={isInitialBalance}
        onSuccess={() => { void navigate({ to: '/' }); }}
      />
    </div>
  );
}

export const Route = createFileRoute('/_app/entries/$id')({
  component: EntryFormPage,
  validateSearch: (search: Record<string, unknown>) => {
    const date = typeof search['date'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search['date'])
      ? search['date']
      : undefined;
    return date ? { date } : {};
  },
});
